import seedData from '../data/contest-catalog-seed.json';
import type {
  ContestAgeSectionContent,
  ContestBenchmark,
  ContestCapabilityLevel,
  ContestCatalogEntry,
  ContestCatalogSeed,
  ContestCategoryId,
  ContestGenderBenchmark,
  ContestGenderKey,
  ContestGradeBand,
  ContestGradeCode,
  ContestMediaItem,
  ContestRelatedVideo,
  ContestSection,
  ContestSectionBlock,
  ContestSectionImage,
  ContestSectionKey,
  ContestTypeId,
  SportContestCatalog,
} from '../types/contestCatalog';

const catalog = seedData as ContestCatalogSeed;

/** "Coach" is a reference tier (coach demonstration standard), not an age band. */
export const CONTEST_AGE_CATEGORIES = [
  'U9',
  'U11',
  'U13',
  'U15',
  'U17',
  'U19',
  'Senior',
  'Para',
  'Coach',
] as const;

export type ContestAgeCategory = (typeof CONTEST_AGE_CATEGORIES)[number];

export const CONTEST_GENDER_KEYS: ContestGenderKey[] = ['boys', 'girls'];

/** Adult tiers also run Open and Gender diverse divisions. */
export const ADULT_CONTEST_GENDER_KEYS: ContestGenderKey[] = ['boys', 'girls', 'open', 'diverse'];

/** Adult age categories use Male/Female labels; juniors use Boy/Girl. */
export function isAdultContestAge(ageCategory: string): boolean {
  return ageCategory === 'Senior' || ageCategory === 'Para' || ageCategory === 'Coach';
}

/** Gender divisions available for an age — juniors are Boy/Girl only. */
export function getContestGenderKeys(ageCategory: string): ContestGenderKey[] {
  return isAdultContestAge(ageCategory) ? ADULT_CONTEST_GENDER_KEYS : CONTEST_GENDER_KEYS;
}

export function getContestGenderLabel(ageCategory: string, genderKey: ContestGenderKey): string {
  if (genderKey === 'open') return 'Open';
  if (genderKey === 'diverse') return 'Gender diverse';
  if (isAdultContestAge(ageCategory)) {
    return genderKey === 'boys' ? 'Male' : 'Female';
  }
  return genderKey === 'boys' ? 'Boy' : 'Girl';
}

export function profileGenderToContestKey(
  gender: string | null | undefined,
  ageCategory?: string | null,
): ContestGenderKey | null {
  const g = String(gender || '').trim().toLowerCase();
  if (g === 'male' || g === 'boy' || g === 'boys' || g === 'm') return 'boys';
  if (g === 'female' || g === 'girl' || g === 'girls' || g === 'f') return 'girls';
  if (g === 'other' || g === 'diverse' || g === 'open') {
    return isAdultContestAge(String(ageCategory || '')) ? 'diverse' : null;
  }
  return null;
}

/** Age band from date of birth. Returns null when DOB is missing. */
export function calculateContestAgeCategory(dateOfBirth?: string | null): string | null {
  if (!dateOfBirth) return null;
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age -= 1;
  if (age < 9) return 'U9';
  if (age < 11) return 'U11';
  if (age < 13) return 'U13';
  if (age < 15) return 'U15';
  if (age < 17) return 'U17';
  if (age < 19) return 'U19';
  return 'Senior';
}

export function contestAgeCategoryFromProfile(profile?: {
  dateOfBirth?: string | null;
  ageCategory?: string | null;
} | null): string | null {
  if (!profile) return null;
  return calculateContestAgeCategory(profile.dateOfBirth) || String(profile.ageCategory || '').trim() || null;
}

export function isProfileContestAge(profileAge: string | null | undefined, selectedAge: string): boolean {
  if (!profileAge) return true;
  return String(profileAge).trim().toLowerCase() === String(selectedAge || '').trim().toLowerCase();
}

export const CONTEST_TYPE_OPTIONS: { id: ContestTypeId; label: string; adminLabel: string }[] = [
  {
    id: 'foundation',
    label: 'Developmental & Performance Capabilities',
    adminLabel: 'Developmental & Performance Contest',
  },
  // Shipped later — same sport page, different contest type tab/filter
  // { id: 'advanced', label: 'Advanced Skills', adminLabel: 'Advanced Skills Contest' },
  // { id: 'elite', label: 'Elite Skills', adminLabel: 'Elite Skills Contest' },
];

export const CONTEST_CAPABILITY_LEVELS: ContestCapabilityLevel[] = ['Fundamental', 'Advanced', 'Elite'];

export const DEFAULT_CAPABILITY_LEVEL: ContestCapabilityLevel = 'Fundamental';

/** Capability Level palette — Fundamental (Slate Blue), Advanced (Teal/Cyan), Elite (Electric Gold). */
export const CAPABILITY_LEVEL_COLORS: Record<
  ContestCapabilityLevel,
  { bg: string; text: string; border: string }
> = {
  Fundamental: { bg: '#D6DEEA', text: '#3D4F6F', border: '#6B7C99' },
  Advanced: { bg: '#CFFAFE', text: '#0E7490', border: '#22D3EE' },
  Elite: { bg: '#FDE047', text: '#854D0E', border: '#EAB308' },
};

export function normalizeCapabilityLevel(value?: string | null): ContestCapabilityLevel {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'advanced') return 'Advanced';
  if (raw === 'elite') return 'Elite';
  return DEFAULT_CAPABILITY_LEVEL;
}

export function getCapabilityLevelColor(level?: string | null) {
  return CAPABILITY_LEVEL_COLORS[normalizeCapabilityLevel(level)];
}

export function getContestTypeLabel(id: ContestTypeId): string {
  return CONTEST_TYPE_OPTIONS.find((o) => o.id === id)?.label ?? id;
}

export function getContestTypeAdminLabel(id: ContestTypeId): string {
  return CONTEST_TYPE_OPTIONS.find((o) => o.id === id)?.adminLabel ?? getContestTypeLabel(id);
}

export const CONTEST_GRADE_ORDER: ContestGradeCode[] = ['D', 'C', 'B', 'A', 'A+', 'S'];

/** Default platform grade sequence (lowest → highest). */
export const PLATFORM_GRADE_ORDER: ContestGradeCode[] = ['D', 'F', 'C', 'B', 'A', 'A+', 'S'];

/** Kept in data for later; hidden from scorecards, certificates, and public grade lists. */
export const HIDDEN_CONTEST_GRADES: ContestGradeCode[] = ['S'];

export function isHiddenContestGrade(code: string | null | undefined): boolean {
  return HIDDEN_CONTEST_GRADES.includes(String(code || '').toUpperCase() as ContestGradeCode);
}

/** Map a stored hidden grade to the current top visible grade. */
export function toVisibleContestGrade(code: string | null | undefined): string {
  if (!code) return '';
  return isHiddenContestGrade(code) ? 'A+' : String(code);
}

export function visibleContestGradeCodes(codes: string[] = CONTEST_GRADE_ORDER): string[] {
  return codes.filter((code) => !isHiddenContestGrade(code));
}

export function sortContestGradeCodes(codes: string[]): string[] {
  const rank = new Map(PLATFORM_GRADE_ORDER.map((c, i) => [String(c), i]));
  return [...codes].sort((a, b) => {
    const ra = rank.has(a) ? (rank.get(a) as number) : 1000;
    const rb = rank.has(b) ? (rank.get(b) as number) : 1000;
    return ra - rb || a.localeCompare(b);
  });
}

export const CONTEST_GRADE_LABELS: Record<ContestGradeCode, string> = {
  D: 'Grade D',
  F: 'Grade F',
  C: 'Grade C',
  B: 'Grade B',
  A: 'Grade A',
  'A+': 'Grade A+',
  S: 'Grade S',
};

export const CONTEST_CATEGORY_OPTIONS: { id: ContestCategoryId; label: string }[] = [
  { id: 'skill', label: 'Skill' },
  { id: 'drill', label: 'Drill' },
  { id: 'fitness', label: 'Fitness' },
  { id: 'tactical', label: 'Tactical' },
  { id: 'match_intelligence', label: 'Match Intelligence' },
];

export const CONTEST_SECTION_ORDER: { key: ContestSectionKey; label: string }[] = [
  { key: 'liability_disclaimer', label: 'Online Contest Liability Disclaimer & Safety Rules' },
  { key: 'scoring', label: 'Scoring' },
  { key: 'equipment_specs', label: 'Technical Specifications' },
  { key: 'drills', label: 'Drills' },
  { key: 'safety_guidelines', label: 'Safety Guidelines' },
  { key: 'injury_prevention', label: 'Injury Prevention' },
  { key: 'motor_psychomotor', label: 'Motor & Psychomotor' },
  { key: 'tactics', label: 'Tactics' },
  { key: 'match_intelligence', label: 'Match Intelligence' },
  { key: 'coaching', label: 'Coaching' },
  { key: 'adults_vs_children', label: 'Adults vs Children' },
  { key: 'parents_corner', label: 'Parents Corner' },
];

/** Always first on Learn; cannot be hidden, so numbering has a stable 1.0. */
export const REQUIRED_FRAME2_SECTION_KEYS: ContestSectionKey[] = ['liability_disclaimer'];

export function isRequiredFrame2Section(key: ContestSectionKey): boolean {
  return REQUIRED_FRAME2_SECTION_KEYS.includes(key);
}

export function getVisibleFrame2Sections(
  contest: Pick<ContestCatalogEntry, 'sections'> | null | undefined,
): { key: ContestSectionKey; label: string; visibleIndex: number }[] {
  const byKey = new Map((contest?.sections || []).map((section) => [section.key, section]));
  const visible: { key: ContestSectionKey; label: string; visibleIndex: number }[] = [];
  CONTEST_SECTION_ORDER.forEach((meta) => {
    const row = byKey.get(meta.key);
    if (row?.hidden && !isRequiredFrame2Section(meta.key)) return;
    visible.push({
      key: meta.key,
      label: row?.label || meta.label,
      visibleIndex: visible.length,
    });
  });
  return visible;
}

export function getFrame2SectionVisibleIndex(
  contest: Pick<ContestCatalogEntry, 'sections'> | null | undefined,
  sectionKey: ContestSectionKey,
): number {
  return getVisibleFrame2Sections(contest).find((row) => row.key === sectionKey)?.visibleIndex ?? 0;
}

export function getSportContestCatalog(sport: string): SportContestCatalog | null {
  return catalog.sports[sport] ?? null;
}

export function hasContestCatalogPreview(sport: string): boolean {
  return Boolean(catalog.sports[sport]);
}

export function getContestsForSelection(
  sportCatalog: SportContestCatalog,
  contestTypeId: ContestTypeId,
  categoryId: ContestCategoryId
): ContestCatalogEntry[] {
  const typeGroup = sportCatalog.contestTypes.find((t) => t.id === contestTypeId);
  const categoryGroup = typeGroup?.categories.find((c) => c.id === categoryId);
  return categoryGroup?.contests ?? [];
}

export interface ContestCategoryGroup {
  id: ContestCategoryId;
  label: string;
  contests: ContestCatalogEntry[];
}

export function getContestsGroupedByCategory(
  sportCatalog: SportContestCatalog,
  contestTypeId: ContestTypeId
): ContestCategoryGroup[] {
  const typeGroup = sportCatalog.contestTypes.find((t) => t.id === contestTypeId);
  if (!typeGroup) return [];
  const seen = new Set<string>();
  return typeGroup.categories
    .map((cat) => ({
      id: cat.id,
      label: cat.label,
      contests: (cat.contests || []).filter((contest) => {
        const id = String(contest?.id || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      }),
    }))
    .filter((group) => group.contests.length > 0);
}

export function findContestInCatalog(
  sportCatalog: SportContestCatalog,
  contestTypeId: ContestTypeId,
  contestId: string
): { contest: ContestCatalogEntry; categoryId: ContestCategoryId } | null {
  const groups = getContestsGroupedByCategory(sportCatalog, contestTypeId);
  for (const group of groups) {
    const contest = group.contests.find((c) => c.id === contestId);
    if (contest) return { contest, categoryId: group.id };
  }
  return null;
}

export function getBenchmarkForAge(
  contest: ContestCatalogEntry,
  ageCategory: string
): ContestBenchmark | null {
  return contest.benchmarks[ageCategory] ?? contest.benchmarks.Senior ?? null;
}

function normalizeTimeUnitLabel(unit: string | undefined | null): string {
  const u = String(unit || 'min').trim().toLowerCase();
  if (u === 'second' || u === 'seconds' || u === 's' || u === 'sec') return 'sec';
  if (u === 'minute' || u === 'minutes' || u === 'min' || u === 'm') return 'min';
  return String(unit || 'min').trim();
}

export function getTargetTimeDisplay(
  contest: ContestCatalogEntry,
  ageCategory: string,
  genderKey?: ContestGenderKey | null,
): { label: string; title: string } {
  const bench = getBenchmarkForAge(contest, ageCategory);
  const gendered = genderKey ? bench?.byGender?.[genderKey] : undefined;
  // Divisions without their own target (e.g. Open before it is authored) borrow the shared
  // value; take the unit from the same source so a "sec" target never reads as "min".
  const donor = gendered ?? bench?.byGender?.boys ?? bench?.byGender?.girls;
  const raw = gendered?.recommendedMaxTime ?? bench?.recommendedMaxTime ?? donor?.recommendedMaxTime;
  if (raw == null || raw === '') {
    return { label: '—', title: 'Target time not set for this age' };
  }
  const unit = normalizeTimeUnitLabel(gendered?.timeUnit ?? bench?.timeUnit ?? donor?.timeUnit);
  const label = `${raw} ${unit}`;
  const who = genderKey ? ` (${getContestGenderLabel(ageCategory, genderKey)})` : '';
  return { label, title: `Target time${who}: ${label}` };
}

/** Grade codes for scorecard columns — full platform order D→A+ (S hidden), plus any extra published codes. Empty cells stay "—". */
export function resolveScorecardGradeCodes(
  contests: ContestCatalogEntry[],
  ageCategory: string,
  genderKey?: ContestGenderKey | null,
): string[] {
  const codes = new Set<string>(visibleContestGradeCodes());
  for (const contest of contests) {
    const slices: ContestGradeBand[][] = genderKey
      ? [getGradeBandsForAge(contest, ageCategory, genderKey)]
      : [
          getGradeBandsForAge(contest, ageCategory, 'boys'),
          getGradeBandsForAge(contest, ageCategory, 'girls'),
          getGradeBandsForAge(contest, ageCategory),
        ];
    for (const bands of slices) {
      bands.forEach((b) => {
        if (b?.code) codes.add(String(b.code));
      });
    }
  }
  return visibleContestGradeCodes(sortContestGradeCodes([...codes]));
}

function resolveGenderSlice(
  bench: ContestBenchmark | null,
  genderKey?: ContestGenderKey | null,
): ContestGenderBenchmark | null {
  if (!bench?.byGender || !genderKey) return null;
  const slice = bench.byGender[genderKey];
  if (!slice) return null;
  // Backward compat if an older in-memory shape stored grades arrays directly
  if (Array.isArray(slice)) {
    return { grades: slice as ContestGradeBand[] };
  }
  return slice;
}

/** Grade bands for the selected age (+ optional gender). Falls back to shared/legacy grades. */
export function getGradeBandsForAge(
  contest: ContestCatalogEntry,
  ageCategory: string,
  genderKey?: ContestGenderKey | null,
  options?: { fallbackOtherGender?: boolean },
): ContestGradeBand[] {
  const bench = getBenchmarkForAge(contest, ageCategory);
  const gendered = resolveGenderSlice(bench, genderKey);
  const boys = resolveGenderSlice(bench, 'boys');
  const girls = resolveGenderSlice(bench, 'girls');
  const shared = bench?.grades;
  const fallbackOtherGender = options?.fallbackOtherGender !== false;

  const mergeBands = (
    primary?: ContestGradeBand[] | null,
    ...fallbacks: (ContestGradeBand[] | null | undefined)[]
  ): ContestGradeBand[] => {
    const codes = visibleContestGradeCodes(sortContestGradeCodes([
      ...new Set(
        [primary, ...fallbacks]
          .flatMap((list) => list || [])
          .map((b) => String(b.code))
          .filter(Boolean),
      ),
    ]));
    if (!codes.length) return [];
    return codes.map((code) => {
      const fromPrimary = primary?.find((b) => String(b.code) === code);
      if (fromPrimary && fromPrimary.label && fromPrimary.label !== '—') return fromPrimary;
      for (const list of fallbacks) {
        const hit = list?.find((b) => String(b.code) === code);
        if (hit && hit.label && hit.label !== '—') return hit;
      }
      return fromPrimary || { code: code as ContestGradeCode, label: '—' };
    });
  };

  if (genderKey) {
    const other = genderKey === 'boys' ? girls : boys;
    const merged = fallbackOtherGender
      ? mergeBands(gendered?.grades, shared, other?.grades)
      : mergeBands(gendered?.grades, shared);
    if (merged.some((b) => b.label && b.label !== '—')) return merged;
  }

  if (shared && shared.length > 0) {
    const merged = mergeBands(shared, boys?.grades, girls?.grades);
    if (merged.length) return merged;
  }

  if (bench?.byGender) {
    const fallback = mergeBands(boys?.grades, girls?.grades, shared);
    if (fallback.length) return fallback;
  }

  // Fallback from legacy single targetValue
  const raw = bench?.targetValue;
  if (!raw) {
    return visibleContestGradeCodes().map((code) => ({ code: code as ContestGradeCode, label: '—' }));
  }
  const n = Number.parseFloat(String(raw).replace(/[^\d.]/g, ''));
  if (!Number.isFinite(n)) {
    return visibleContestGradeCodes().map((code) => ({ code: code as ContestGradeCode, label: String(raw) }));
  }
  // Higher-is-better default bands around the legacy target (C/B/A only)
  const cMax = Math.max(1, Math.round(n * 0.7));
  const bMin = cMax + 1;
  const bMax = Math.max(bMin, Math.round(n * 0.9));
  const aMin = bMax + 1;
  return [
    { code: 'C', label: `≤${cMax}`, min: 0, max: cMax },
    { code: 'B', label: `${bMin}–${bMax}`, min: bMin, max: bMax },
    { code: 'A', label: `${aMin}+`, min: aMin, max: null },
  ];
}

export function getBenchmarkUnit(contest: ContestCatalogEntry, ageCategory: string): string {
  return getBenchmarkForAge(contest, ageCategory)?.unit ?? '';
}

export function getBenchmarkMetricType(
  contest: ContestCatalogEntry,
  ageCategory: string
): ContestBenchmark['metricType'] {
  return getBenchmarkForAge(contest, ageCategory)?.metricType;
}

export interface GradeAssessmentResult {
  grade: ContestGradeCode | null;
  label: string;
  message: string;
  matchedBand: ContestGradeBand | null;
}

/**
 * Map a self-entered performance value to grade C / B / A using published ranges.
 * Time metrics use lower-is-better (A has max ceiling); others use higher-is-better.
 */
export function assessGradeFromValue(
  contest: ContestCatalogEntry,
  ageCategory: string,
  rawValue: number,
  genderKey?: ContestGenderKey | null,
): GradeAssessmentResult {
  const bands = getGradeBandsForAge(contest, ageCategory, genderKey);
  const metricType = getBenchmarkMetricType(contest, ageCategory);
  const unit = getBenchmarkUnit(contest, ageCategory);
  const unitSuffix = unit ? ` ${unit}` : '';
  const genderLabel = genderKey ? getContestGenderLabel(ageCategory, genderKey) : '';
  const contextLabel = genderLabel ? `${ageCategory} · ${genderLabel}` : ageCategory;

  if (!Number.isFinite(rawValue)) {
    return {
      grade: null,
      label: 'Invalid',
      message: 'Enter a valid number to assess your performance.',
      matchedBand: null,
    };
  }

  const lowerIsBetter = metricType === 'time';

  const inBand = (band: ContestGradeBand): boolean => {
    const min = band.min;
    const max = band.max;
    if (min == null && max == null) return false;
    if (lowerIsBetter) {
      // A often: min=0, max=ceiling; B/C: min–max ranges (slower)
      if (max == null && min != null) return rawValue >= min;
      if (min == null && max != null) return rawValue <= max;
      return rawValue >= (min ?? -Infinity) && rawValue <= (max as number);
    }
    // Higher is better: A often min with max null
    if (max == null && min != null) return rawValue >= min;
    if (min == null && max != null) return rawValue <= max;
    return rawValue >= (min ?? -Infinity) && rawValue <= (max as number);
  };

  // Prefer highest grade first (S → … → D) so overlapping edges pick the best band
  const bandCodes = bands.map((b) => String(b.code));
  const assessOrder = [...PLATFORM_GRADE_ORDER]
    .reverse()
    .filter((code) => bandCodes.includes(code) && !isHiddenContestGrade(code));
  for (const code of assessOrder) {
    const band = bands.find((b) => b.code === code);
    if (band && inBand(band)) {
      const visible = toVisibleContestGrade(code);
      return {
        grade: visible as ContestGradeCode,
        label: `Grade ${visible}`,
        message: `Your result ${rawValue}${unitSuffix} places you in Grade ${visible} (${band.label}${unitSuffix.trim() ? ` ${unit}` : ''}) for ${contextLabel}.`,
        matchedBand: band,
      };
    }
  }

  // Outside published bands → clamp: above top = best grade (e.g. A+), below bottom = worst (e.g. D)
  const orderedBands = assessOrder
    .map((code) => bands.find((b) => String(b.code) === code))
    .filter((b): b is ContestGradeBand => Boolean(b));
  if (!orderedBands.length) {
    return {
      grade: null,
      label: 'Outside bands',
      message: `Your result ${rawValue}${unitSuffix} is outside the published grade ranges for ${contextLabel}.`,
      matchedBand: null,
    };
  }

  const topBand = orderedBands[0];
  const bottomBand = orderedBands[orderedBands.length - 1];
  const topCode = toVisibleContestGrade(topBand.code) as ContestGradeCode;
  const bottomCode = toVisibleContestGrade(bottomBand.code) as ContestGradeCode;

  const numericEdges = orderedBands.flatMap((b) => {
    const vals: number[] = [];
    if (b.min != null && Number.isFinite(b.min)) vals.push(b.min);
    if (b.max != null && Number.isFinite(b.max)) vals.push(b.max);
    return vals;
  });

  if (!numericEdges.length) {
    return {
      grade: null,
      label: 'Pending benchmarks',
      message: `Grade ranges are not published yet for ${contextLabel}. Your score is saved and the grade will update when benchmarks are added or revised.`,
      matchedBand: null,
    };
  }

  const lo = Math.min(...numericEdges);
  const hi = Math.max(...numericEdges);

  // Higher-is-better: above hi → top; below lo → bottom
  // Lower-is-better (time): below lo → top; above hi → bottom
  const assignTop = lowerIsBetter ? rawValue < lo : rawValue > hi;
  const clampBand = assignTop ? topBand : bottomBand;
  const clampCode = assignTop ? topCode : bottomCode;
  const clampPhrase = assignTop
    ? `exceeds the ${clampCode} band`
    : `is below the ${clampCode} band`;

  return {
    grade: clampCode,
    label: `Grade ${clampCode}`,
    message: `Your result ${rawValue}${unitSuffix} ${clampPhrase} for ${contextLabel} — recorded as Grade ${clampCode}.`,
    matchedBand: clampBand,
  };
}

export function getMediaForSection(
  contest: ContestCatalogEntry,
  sectionKey: ContestSectionKey
) {
  return enrichMediaWithAgeCompareVariants(
    contest.media.filter((item) => item.sectionKey === sectionKey),
  );
}

/** Hierarchical Frame 3 numbers: primary 1.0, related 1.1 / 1.2, next primary 2.0. */
export function formatContestVideoNumber(primaryIndex: number, relatedIndex?: number): string {
  const major = primaryIndex + 1;
  if (relatedIndex == null || relatedIndex < 0) return `${major}.0`;
  return `${major}.${relatedIndex + 1}`;
}

/** Frame 2 text blocks share the visible-section major number: 1.0 then 1.1 / 1.2. Hidden sections are skipped. */
export function formatContestSectionBlockNumber(sectionIndex: number, blockIndex: number): string {
  return blockIndex <= 0
    ? formatContestVideoNumber(sectionIndex)
    : formatContestVideoNumber(sectionIndex, blockIndex - 1);
}

export function newContestSectionBlock(): ContestSectionBlock {
  return {
    id: `blk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: '',
    body: '',
    images: [],
  };
}

export function cloneContestSectionBlocks(blocks?: ContestSectionBlock[]): ContestSectionBlock[] {
  return (blocks || []).map((block) => ({
    id: block.id || newContestSectionBlock().id,
    title: block.title || '',
    body: block.body || '',
    images: (block.images || []).map((image) => ({
      url: image.url || '',
      caption: image.caption || '',
    })),
  }));
}

export function blocksFromLegacyContent(content?: string, bullets?: string[]): ContestSectionBlock[] {
  const parts: string[] = [];
  const text = String(content || '').trim();
  if (text) parts.push(text);
  const list = (bullets || []).map((bullet) => String(bullet || '').trim()).filter(Boolean);
  if (list.length) parts.push(list.map((bullet) => `- ${bullet}`).join('\n'));
  const body = parts.join('\n\n');
  if (!body) return [];
  return [{ ...newContestSectionBlock(), body }];
}

export function hydrateSectionBlocks(
  source?: Pick<ContestAgeSectionContent, 'content' | 'bullets' | 'blocks'> | ContestSection | null,
): ContestSectionBlock[] {
  if (source?.blocks && source.blocks.length) return cloneContestSectionBlocks(source.blocks);
  return blocksFromLegacyContent(source?.content, source?.bullets);
}

export function sectionBlocksHaveContent(blocks?: ContestSectionBlock[]): boolean {
  return (blocks || []).some(
    (block) =>
      String(block.title || '').trim() ||
      String(block.body || '').trim() ||
      (block.images || []).some((image) => String(image.url || '').trim()),
  );
}

export function compactSectionBlocks(blocks?: ContestSectionBlock[]): ContestSectionBlock[] | undefined {
  const out = cloneContestSectionBlocks(blocks)
    .map((block) => ({
      ...block,
      title: String(block.title || '').trim() || undefined,
      body: String(block.body || '').trim(),
      images: (block.images || [])
        .map((image) => ({
          url: String(image.url || '').trim(),
          caption: String(image.caption || '').trim() || undefined,
        }))
        .filter((image) => image.url) as ContestSectionImage[],
    }))
    .filter((block) => block.title || block.body || (block.images && block.images.length));
  return out.length ? out : undefined;
}

export function legacyFromSectionBlocks(blocks?: ContestSectionBlock[]): { content: string; bullets: string[] } {
  const compacted = compactSectionBlocks(blocks) || [];
  if (!compacted.length) return { content: '', bullets: [] };
  const first = compacted[0];
  const rest = compacted.slice(1).map((block) => {
    const heading = block.title ? `## ${block.title}` : '';
    return [heading, block.body].filter(Boolean).join('\n\n');
  });
  return {
    content: [first.title ? `## ${first.title}` : '', first.body, ...rest].filter(Boolean).join('\n\n'),
    bullets: [],
  };
}

export interface ResolvedMediaClip {
  url: string;
  title: string;
  notes?: string;
  relatedVideos?: ContestRelatedVideo[];
  ageCategory: string;
  fromAgeVariant: boolean;
}

/** Resolve a media item (or related clip) for a selected age — falls back to base url/title. */
export function resolveMediaClipForAge(
  item: Pick<ContestMediaItem, 'title' | 'url' | 'notes' | 'relatedVideos' | 'byAge'>,
  ageCategory: string,
): ResolvedMediaClip {
  const age = String(ageCategory || '').trim();
  const variant = (age && item.byAge?.[age]) || (age && item.byAge?.Senior) || null;
  if (variant?.url) {
    return {
      url: variant.url,
      title: variant.title || item.title,
      notes: variant.notes ?? item.notes,
      relatedVideos: variant.relatedVideos ?? item.relatedVideos,
      ageCategory: age || 'Senior',
      fromAgeVariant: true,
    };
  }
  return {
    url: item.url,
    title: item.title,
    notes: item.notes,
    relatedVideos: item.relatedVideos,
    ageCategory: age || 'Senior',
    fromAgeVariant: false,
  };
}

export function mediaHasAgeVariants(
  item: Pick<ContestMediaItem, 'byAge'> | null | undefined,
): boolean {
  return Boolean(item?.byAge && Object.keys(item.byAge).length > 0);
}

/** Ages available for video compare: explicit byAge keys, else foundation age list. */
export function getMediaCompareAges(
  item: Pick<ContestMediaItem, 'byAge'> | null | undefined,
): string[] {
  const keys = item?.byAge ? Object.keys(item.byAge) : [];
  if (keys.length) {
    const ordered = CONTEST_AGE_CATEGORIES.filter((a) => keys.includes(a));
    const extras = keys.filter((k) => !ordered.includes(k));
    return [...ordered, ...extras];
  }
  return [...CONTEST_AGE_CATEGORIES];
}

/**
 * Ensure learning clips expose age compare keys.
 * Uses explicit byAge when present; otherwise maps base + related URLs across foundation ages
 * so Frame 3 can always offer age comparison until distinct age media is authored.
 */
export function enrichMediaWithAgeCompareVariants(media: ContestMediaItem[]): ContestMediaItem[] {
  return (media || []).map((item) => {
    if (item.byAge && Object.keys(item.byAge).length > 0) return item;
    const pool = [
      { url: item.url, title: item.title, notes: item.notes },
      ...(item.relatedVideos || []).map((r) => ({
        url: r.url,
        title: r.title || item.title,
        notes: r.notes,
      })),
    ].filter((c) => c.url);
    if (!pool.length) return item;
    const byAge: NonNullable<ContestMediaItem['byAge']> = {};
    CONTEST_AGE_CATEGORIES.forEach((age, idx) => {
      const clip = pool[idx % pool.length];
      byAge[age] = {
        url: clip.url,
        title: `${clip.title} · ${age}`,
        notes: clip.notes,
      };
    });
    return { ...item, byAge };
  });
}

const MEDIA_ROLE_ORDER: Record<string, number> = {
  primary: 0,
  reference: 1,
  sample: 2,
};

export interface OrderedContestVideo {
  id: string;
  title: string;
  url: string;
  sequence: number;
  /** Hierarchical label such as 1.0 or 1.1 */
  numberLabel: string;
  role?: ContestCatalogEntry['media'][number]['role'];
  isRelated?: boolean;
  parentTitle?: string;
  notes?: string;
}

/** Scoring/reference playlist for a skill — primary → reference → sample, related clips follow each parent. */
export function getOrderedReferenceVideos(contest: ContestCatalogEntry): OrderedContestVideo[] {
  const scoringMedia = getMediaForSection(contest, 'scoring');
  const sorted = [...scoringMedia].sort((a, b) => {
    const roleA = MEDIA_ROLE_ORDER[a.role ?? ''] ?? 3;
    const roleB = MEDIA_ROLE_ORDER[b.role ?? ''] ?? 3;
    if (roleA !== roleB) return roleA - roleB;
    return 0;
  });

  const ordered: OrderedContestVideo[] = [];
  let sequence = 0;

  sorted.forEach((video, primaryIndex) => {
    sequence += 1;
    ordered.push({
      id: video.id,
      title: video.title,
      url: video.url,
      sequence,
      numberLabel: formatContestVideoNumber(primaryIndex),
      role: video.role,
      notes: video.notes,
    });
    (video.relatedVideos || []).forEach((related, relatedIndex) => {
      sequence += 1;
      ordered.push({
        id: related.id,
        title: related.title,
        url: related.url,
        sequence,
        numberLabel: formatContestVideoNumber(primaryIndex, relatedIndex),
        isRelated: true,
        parentTitle: video.title,
        notes: related.notes,
      });
    });
  });

  return ordered;
}

export function getSectionByKey(contest: ContestCatalogEntry, sectionKey: ContestSectionKey) {
  return contest.sections.find((s) => s.key === sectionKey);
}

const PLACEHOLDER_SECTION_CONTENT = 'Detailed content will be published from admin.';

const FRAME2_DEFAULT_SECTIONS: Partial<
  Record<ContestSectionKey, { label: string; content: string; bullets: string[] }>
> = {
  liability_disclaimer: {
    label: 'Online Contest Liability Disclaimer & Safety Rules',
    content:
      'Participation is voluntary. EncycGames publishes skill challenges and educational guidance only — not medical, coaching, or legal advice. Players, parents, and coaches are responsible for a safe space, age-appropriate equipment, and stopping at the first sign of pain or danger. By attempting this contest you accept the risks of sport activity and agree to follow these rules.',
    bullets: [
      'Warm up, use suitable equipment, and film in a clear, obstacle-free area',
      'Stop immediately if you feel pain, dizziness, or loss of control',
      'Minors must have a parent or guardian aware of the attempt',
      'Follow local laws, facility rules, and any coach instructions',
      'EncycGames and contest organizers are not liable for injury, property damage, or misuse of this guidance',
    ],
  },
  safety_guidelines: {
    label: 'Safety Guidelines',
    content:
      'Self-evaluation uses the same age-wise participation and safety rules as the contest. Attempt only what is appropriate for the selected age category, with suitable equipment, space, and supervision.',
    bullets: [
      'Warm up; use age-appropriate equipment and a clear, obstacle-free space',
      'Minors should have a parent, guardian, or coach aware of the attempt',
      'Do not chase grades with high-intensity or high-volume training',
      'Stop immediately at pain, dizziness, or loss of control',
    ],
  },
  motor_psychomotor: {
    label: 'Motor & Psychomotor',
    content:
      'Every Technical Foundation skill sits on EncycGames movement and psychomotor framework: fundamental movement skills, broader movement patterns, sport movement quality, object control, and perceptual-motor control (tracking, timing, reaction). Capabilities map to this sport as Direct, Transfer, Limited, or N/A.',
    bullets: [
      'Traditional FMS — locomotor, stability, and object-control patterns',
      'Broad and sport movement foundation — landing, bracing, acceleration, change of direction',
      'Perceptual-motor — tracking, scanning, reaction, timing, and coordination',
    ],
  },
  coaching: {
    label: 'Coaching',
    content:
      'Use this skill as a short coaching checkpoint: demonstrate the standard, cue one focus point, then let the player attempt under the contest time and scoring rules.',
    bullets: [
      'Demo once at contest pace, then freeze the key position for a clear cue',
      'Give one primary cue per attempt (footwork, contact, or follow-through)',
      'Film from the angle that shows the scoring criteria most clearly',
      'Debrief with the grade band — what earned the score, what to train next',
      'Keep sessions short: quality attempts beat high-volume repetition',
    ],
  },
  adults_vs_children: {
    label: 'Adults vs Children',
    content:
      'The same Foundation skill applies across ages, but load, language, and expectations differ for children and adults.',
    bullets: [
      'Children: shorter cues, more rest, emphasize form and enjoyment over peak score',
      'Adults: higher volume tolerance, but still protect joints and warm up thoroughly',
      'Youth attempts should use age-band targets (U9–U19), not Senior standards',
      'Adult / Senior / Open divisions use adult labels (Male/Female) and adult recovery norms',
      'Never push a child toward adult intensity just to chase a higher grade band',
    ],
  },
};

function sectionOrderLabel(sectionKey: ContestSectionKey): string {
  return CONTEST_SECTION_ORDER.find((s) => s.key === sectionKey)?.label || sectionKey;
}

/** Resolve section content for the selected age (falls back to base section copy) */
export function getSectionContentForAge(
  contest: ContestCatalogEntry,
  sectionKey: ContestSectionKey,
  ageCategory: string
): { label: string; content: string; bullets: string[]; blocks: ContestSectionBlock[] } | null {
  const defaults = FRAME2_DEFAULT_SECTIONS[sectionKey];
  const section = getSectionByKey(contest, sectionKey);
  if (!section) {
    return defaults ? { ...defaults, blocks: hydrateSectionBlocks(defaults) } : null;
  }
  const ageBlock = section.byAge?.[ageCategory] ?? section.byAge?.Senior;
  const content = ageBlock?.content ?? section.content;
  const bullets = ageBlock?.bullets ?? section.bullets ?? [];
  const blocksSource = ageBlock?.blocks?.length
    ? ageBlock
    : section.blocks?.length
      ? section
      : { content, bullets };
  if (defaults && (!content || content === PLACEHOLDER_SECTION_CONTENT) && !sectionBlocksHaveContent(ageBlock?.blocks) && !sectionBlocksHaveContent(section.blocks)) {
    return { ...defaults, blocks: hydrateSectionBlocks(defaults) };
  }
  return {
    label: section.label || defaults?.label || sectionOrderLabel(sectionKey),
    content,
    bullets,
    blocks: hydrateSectionBlocks(blocksSource),
  };
}
